import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useFollows } from './useFollows'

// ── Centralized friends-watching cache ───────────────────────────────────────
// Returns a Map<show_id, Friend[]> indexed by show, where each Friend includes
// their current progress on that show. Powers the "Watching With" indicator
// on ShowCard and ShowDetail.
//
// Privacy: only profiles with is_public=true are included. Profiles still
// show in your own My List etc., they just don't broadcast their progress
// to others.
//
// Caching pattern mirrors useFollows: module-level cache, deduped in-flight
// promises, stale-while-revalidate on subsequent calls. One query covers all
// followed users' watching/rewatching rows.

const cache = new Map()        // user_id → Map<show_id, Friend[]>
const pending = new Map()      // user_id → Promise<Map<show_id, Friend[]>>
const lastFetch = new Map()    // user_id → epoch ms (for staleness)
const subscribers = new Set()

const STALE_MS = 60_000        // refetch in background if older than 60s

async function fetchFriendsWatching(userId, followingIds) {
  if (!userId || !followingIds?.length) return new Map()
  if (pending.has(userId)) return pending.get(userId)

  const p = (async () => {
    // Pull every public friend's watching/rewatching rows, plus their profile
    const [watchingRes, profilesRes] = await Promise.all([
      supabase.from('public_watchlist')
        .select('user_id, show_id, status, progress, updated_at')
        .in('user_id', followingIds)
        .in('status', ['watching', 'rewatching']),
      supabase.from('profiles')
        .select('id, username, display_name, avatar_url, is_public')
        .in('id', followingIds),
    ])

    const profileMap = new Map()
    for (const p of (profilesRes.data || [])) {
      if (p.is_public) profileMap.set(p.id, p)
    }

    const byShow = new Map()
    for (const w of (watchingRes.data || [])) {
      const prof = profileMap.get(w.user_id)
      if (!prof) continue   // not public OR profile fetch missed → skip
      const list = byShow.get(w.show_id) || []
      list.push({
        id: prof.id,
        username: prof.username,
        display_name: prof.display_name,
        avatar_url: prof.avatar_url,
        progress: w.progress || null,
        status: w.status,
        updated_at: w.updated_at,
      })
      byShow.set(w.show_id, list)
    }

    cache.set(userId, byShow)
    lastFetch.set(userId, Date.now())
    pending.delete(userId)
    return byShow
  })().catch(() => {
    pending.delete(userId)
    return cache.get(userId) || new Map()
  })

  pending.set(userId, p)
  return p
}


export function useFriendsWatching(userId) {
  const { followingIds, ready: followsReady } = useFollows(userId)
  const [byShow, setByShow] = useState(() => cache.get(userId) || new Map())
  const [ready, setReady] = useState(() => cache.has(userId))
  const tickRef = useRef(0)

  useEffect(() => {
    if (!userId || !followsReady) {
      if (!userId) { setByShow(new Map()); setReady(true) }
      return
    }
    let cancelled = false

    const load = async () => {
      // If we have a fresh cache, use it immediately and revalidate in background
      const cached = cache.get(userId)
      const fresh = lastFetch.has(userId) && (Date.now() - lastFetch.get(userId)) < STALE_MS
      if (cached && fresh) {
        setByShow(cached); setReady(true)
        return
      }
      // Otherwise fetch
      const m = await fetchFriendsWatching(userId, followingIds)
      if (!cancelled) { setByShow(m); setReady(true) }
    }
    load()

    const onInvalidate = (tick) => {
      if (tick === tickRef.current) return
      tickRef.current = tick
      fetchFriendsWatching(userId, followingIds).then(m => {
        if (!cancelled) setByShow(m)
      })
    }
    subscribers.add(onInvalidate)
    return () => { cancelled = true; subscribers.delete(onInvalidate) }
  }, [userId, followsReady, followingIds.join(',')])

  return { byShow, ready }
}

// Convenience: get friends watching a specific show. Returns an empty
// array (not undefined) so consumers can safely .length / .map.
export function useFriendsOnShow(userId, showId) {
  const { byShow, ready } = useFriendsWatching(userId)
  const friends = byShow.get(showId) || byShow.get(+showId) || []
  return { friends, ready }
}

// Non-hook cache reader — call from non-component code (e.g. inside a saveProgress
// callback) to peek at the most recent friends-on-show list without subscribing.
// Returns [] if the cache is cold for this user.
export function getFriendsOnShow(userId, showId) {
  const byShow = cache.get(userId)
  if (!byShow) return []
  return byShow.get(showId) || byShow.get(+showId) || []
}

// De-dupe firing of in-sync celebrations: once per (show, friend, episode) per session.
// Cleared on page reload. Good enough for v1; if the user genuinely re-syncs at the
// same episode in a later session, we'll fire again, which is fine.
const firedInSync = new Set()
export function hasFiredInSync(showId, friendId, season, episode) {
  return firedInSync.has(`${showId}:${friendId}:${season}:${episode}`)
}
export function markFiredInSync(showId, friendId, season, episode) {
  firedInSync.add(`${showId}:${friendId}:${season}:${episode}`)
}
